<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    markRaw,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef,
  } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import RestaurantsService from '@/api/services/restaurants.service';
  import type {
    ICreateRestaurantRequest,
    ICreatedRestaurant,
  } from '@/api/services/restaurants.service';
  import { StoreSetupService } from '@/api/services/store-setup.service';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2SetupStepBusiness from '@/components/v2/store-setup/business/V2SetupStepBusiness.vue';
  import V2SetupStepLocation from '@/components/v2/store-setup/location/V2SetupStepLocation.vue';
  import V2SetupBottomBar from '@/components/v2/store-setup/shell/V2SetupBottomBar.vue';
  import V2SetupPartnerNotice from '@/components/v2/store-setup/shell/V2SetupPartnerNotice.vue';
  import V2SetupResumeCard from '@/components/v2/store-setup/shell/V2SetupResumeCard.vue';
  import V2SetupShell from '@/components/v2/store-setup/shell/V2SetupShell.vue';
  import V2SetupTopBar from '@/components/v2/store-setup/shell/V2SetupTopBar.vue';
  import { EMPTY_FLOW_BAR } from '@/composition/store-setup/setup-flow-screen';
  import type {
    IFlowBarState,
    IFlowScreenHandle,
  } from '@/composition/store-setup/setup-flow-screen';
  import {
    dismissSetupResume,
    isSetupResumeDismissed,
  } from '@/composition/store-setup/setup-resume-dismissal';
  import { usePlacesSearch } from '@/composition/store-setup/use-places-search';
  import { useStoreSetupDraft } from '@/composition/store-setup/use-store-setup-draft';
  import type { TStoreSetupDraftStep } from '@/composition/store-setup/use-store-setup-draft';
  import { useGtag } from '@/composition/use-gtag';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
  import {
    buildCreateRestaurantRequest,
    storeCreatedEvent,
  } from '@/domain/store-setup/business-step';
  import type {
    ISlugConflict,
    IStoreCreatedEvent,
  } from '@/domain/store-setup/business-step';
  import {
    setupErrorKey,
    slugTakenSuggestions,
  } from '@/domain/store-setup/setup-errors';
  import { storeLinkLabel } from '@/domain/store-setup/store-identity';
  import {
    CREATE_STORE_CONVERSION,
    fireAdsConversion,
  } from '@/domain/store-setup/store-setup-analytics';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IStoreSetupResume } from '@/interfaces/IStoreSetup';
  import type { IUser } from '@/interfaces/IUser';
  import { vueData } from '@/VueData';

  /**
   * `/new` (plan §4.1, §4.2, §4.12): the store does not exist yet, so
   * everything lives in the local draft of this device. Gates first (an
   * unfinished store to resume, the partner notice), then the screens
   * «Ubicación» → «Negocio», each bound to the single bottom bar through its
   * exposed screen handle.
   */
  type TCreateGate = 'loading' | 'resume' | 'ready';

  const WIDE_MIN_WIDTH = 1024;
  /** A slow resume check must not hold the flow back. */
  const RESUME_WAIT_MS = 4000;

  const props = defineProps<{ user: IUser }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const router = useRouter();
  const gtag = useGtag();
  const ui = useUi();
  const { width } = useStableWindowSize();
  const restaurantsService = new RestaurantsService();

  const draftStore = useStoreSetupDraft(props.user.id);
  const draft = draftStore.draft;
  // Raw: its refs must stay refs when the object travels as a prop.
  const places = markRaw(usePlacesSearch(computed(() => draft.value.country)));

  const gate = ref<TCreateGate>('loading');
  const resume = shallowRef<IStoreSetupResume | null>(null);
  const screen = ref<TStoreSetupDraftStep>(draft.value.step);
  /** The mounted screen of the `v-if` chain below (its exposed handle). */
  const activeScreen = ref<IFlowScreenHandle | null>(null);
  /** `POST /restaurants` is running (kept on success: the page is leaving). */
  const creating = ref(false);
  /** «Negocio» is uploading the logo: no create until it lands. */
  const logoUploading = ref(false);
  /** The link the create call refused twice, with the free links it offered. */
  const slugConflict = shallowRef<ISlugConflict | null>(null);
  let restoredToastShown = false;
  let alive = true;

  const wide = computed(() => width.value >= WIDE_MIN_WIDTH);
  const partnerPending = computed(
    () => props.user.isPartner === true && !draft.value.partnerAccepted
  );
  const stepIndex = computed(() => (screen.value === 'location' ? 0 : 1));
  const screenKey = computed(() =>
    gate.value === 'ready'
      ? `${screen.value}:${draft.value.idempotencyKey}`
      : gate.value
  );
  const bar = computed<IFlowBarState>(() =>
    gate.value === 'ready' && activeScreen.value
      ? activeScreen.value.bar
      : EMPTY_FLOW_BAR
  );

  //#region Screens
  function trackStep(step: TStoreSetupDraftStep) {
    gtag.event('setup_step_viewed', { step, entry: 'setup' });
  }

  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** §4.0: blur, let the keyboard go, then swap (the shell scrolls up and focuses the new `<h1>`). */
  async function goTo(target: TStoreSetupDraftStep) {
    blurActive();
    await nextTick();
    if (!alive) return;
    screen.value = target;
    draftStore.patch({ step: target });
    trackStep(target);
  }

  function leave() {
    // The local draft stays: coming back to `/new` resumes it.
    void router.push('/dashboard');
  }

  /** «Empezar de nuevo» of the restored-draft toast. */
  function startOver() {
    // Never while the store is being created: that draft's key is in flight.
    if (!alive || creating.value) return;
    const partnerAccepted = draft.value.partnerAccepted;
    const previous = screen.value;
    blurActive();
    draftStore.reset();
    slugConflict.value = null;
    if (partnerAccepted) draftStore.patch({ partnerAccepted: true });
    places.query.value = '';
    places.search();
    screen.value = 'location';
    if (previous !== 'location') trackStep('location');
  }
  //#endregion

  //#region Gates
  /** The unfinished store to offer; null when there is none, on any error or after 4 s. */
  async function loadResume(): Promise<IStoreSetupResume | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), RESUME_WAIT_MS);
    });
    try {
      return await Promise.race([StoreSetupService.resume(), timeout]);
    } catch {
      return null;
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  function openFlow() {
    gate.value = 'ready';
    trackStep(screen.value);
    if (draftStore.restored && !restoredToastShown) {
      restoredToastShown = true;
      ui.showToast(AlertType.Info, tr('storeSetup.draftRestored'), {
        text: tr('storeSetup.startOver'),
        cb: startOver,
      });
    }
  }

  function continueResume() {
    const target = resume.value;
    if (target) void router.push(`/${target.stringId}/setup`);
  }

  function dismissResume() {
    const target = resume.value;
    if (target) dismissSetupResume(target.restaurantId);
    resume.value = null;
    openFlow();
  }

  function acceptPartner() {
    draftStore.patch({ partnerAccepted: true });
  }
  //#endregion

  //#region Create («Crear mi tienda», §4.2)
  /** Any refusal other than a taken link: the draft and its idempotency key stay for a retry. */
  function showCreateError(error: unknown) {
    const code = getServerErrorCode(error);
    if (code === 'LOCATION_UNRESOLVED') {
      ui.showToast(AlertType.Error, tr('storeSetup.location.geocodeNotFound'));
      void goTo('location');
      return;
    }
    console.error('[store-setup] create failed', error);
    ui.showToast(
      AlertType.Error,
      tr(setupErrorKey(code) ?? 'storeSetup.business.createError')
    );
  }

  /**
   * `POST /restaurants`; a link taken meanwhile (409 `SLUG_TAKEN`) is retried
   * once with the first free link the server offered. Null when it did not
   * create the store (the error is already shown).
   */
  async function submitCreate(
    request: ICreateRestaurantRequest
  ): Promise<ICreatedRestaurant | null> {
    let firstError: unknown;
    try {
      return await restaurantsService.create(request);
    } catch (error) {
      firstError = error;
    }
    if (!alive) return null;
    if (getServerErrorCode(firstError) !== 'SLUG_TAKEN') {
      showCreateError(firstError);
      return null;
    }
    const suggestion = slugTakenSuggestions(firstError)[0];
    if (!suggestion) {
      slugConflict.value = { slug: request.stringId, suggestions: [] };
      return null;
    }
    draftStore.patch({ slug: suggestion, slugEdited: true });
    try {
      const created = await restaurantsService.create({
        ...request,
        stringId: suggestion,
      });
      ui.showToast(
        AlertType.Info,
        tr('storeSetup.business.slugLost', { url: storeLinkLabel(suggestion) })
      );
      return created;
    } catch (error) {
      if (!alive) return null;
      if (getServerErrorCode(error) === 'SLUG_TAKEN')
        slugConflict.value = {
          slug: suggestion,
          suggestions: slugTakenSuggestions(error),
        };
      else showCreateError(error);
      return null;
    }
  }

  /**
   * The store exists: conversions exactly as the legacy modal fired them
   * (§4.13, D28), the local draft goes, and the setup continues on
   * `/{stringId}/setup` (unless the owner already left this page: the resume
   * card brings them back).
   */
  function onCreated(created: ICreatedRestaurant, event: IStoreCreatedEvent) {
    fireAdsConversion(
      CREATE_STORE_CONVERSION,
      vueData.conversion.bind(vueData)
    );
    gtag.event('create', { event_category: 'category' });
    gtag.event('store_created', { ...event });
    draftStore.clear();
    if (!alive) return;
    router.replace(`/${created.stringId}/setup`).catch((error: unknown) => {
      // Still here: a new press replays the same key and returns this store.
      console.error('[store-setup] navigation after create failed', error);
      if (alive) creating.value = false;
    });
  }

  async function createStore() {
    if (creating.value || logoUploading.value) return;
    if (!draft.value.location) {
      void goTo('location');
      return;
    }
    const request = buildCreateRestaurantRequest(draft.value);
    if (!request) return;
    // Taken before the automatic link retry can change the draft.
    const event = storeCreatedEvent(draft.value);
    creating.value = true;
    slugConflict.value = null;
    const created = await submitCreate(request);
    if (created) {
      onCreated(created, event);
      return;
    }
    if (alive) creating.value = false;
  }
  //#endregion

  //#region Bottom bar
  function onPrimary() {
    void activeScreen.value?.primary();
  }

  function onSecondary() {
    void activeScreen.value?.secondary();
  }

  function onBack() {
    void activeScreen.value?.back();
  }
  //#endregion

  onMounted(async () => {
    const found = await loadResume();
    if (!alive) return;
    const showResume =
      found !== null && !isSetupResumeDismissed(found.restaurantId);
    gtag.event('store_setup_started', {
      partner: props.user.isPartner === true,
      restored: draftStore.restored,
      resumeShown: showResume,
    });
    if (showResume) {
      resume.value = found;
      gate.value = 'resume';
    } else openFlow();
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <V2SetupShell
    class="v2-store-create-page"
    :wide="wide"
    :screen-key="screenKey"
  >
    <template #top>
      <V2SetupTopBar
        :title="$t('storeSetup.title')"
        :step="stepIndex"
        :exit-disabled="creating"
        @exit="leave"
      />
    </template>
    <div v-if="gate === 'loading'" class="create-loading" aria-busy="true">
      <PosUiSkeleton class="loading-title" />
      <PosUiSkeleton class="loading-line" />
      <PosUiSkeleton class="loading-block" />
    </div>
    <div v-else-if="gate === 'resume' && resume" class="create-gate">
      <V2SetupResumeCard
        :resume="resume"
        @continue="continueResume"
        @dismiss="dismissResume"
      />
    </div>
    <template v-else>
      <V2SetupStepLocation
        v-if="screen === 'location'"
        :key="draft.idempotencyKey"
        ref="activeScreen"
        :draft="draft"
        :places="places"
        :wide="wide"
        :disabled="partnerPending"
        @patch="draftStore.patch"
        @done="goTo('business')"
      >
        <template #notice>
          <V2SetupPartnerNotice
            v-if="partnerPending"
            @accept="acceptPartner"
            @back="leave"
          />
        </template>
      </V2SetupStepLocation>
      <V2SetupStepBusiness
        v-else-if="screen === 'business'"
        :key="draft.idempotencyKey"
        ref="activeScreen"
        :draft="draft"
        :wide="wide"
        :busy="creating"
        :slug-conflict="slugConflict"
        @patch="draftStore.patch"
        @uploading="logoUploading = $event"
        @submit="createStore"
        @back="goTo('location')"
      />
    </template>
    <template #bottom>
      <V2SetupBottomBar
        v-show="gate === 'ready'"
        :state="bar"
        :wide="wide"
        @back="onBack"
        @left="onBack"
        @secondary="onSecondary"
        @primary="onPrimary"
      />
    </template>
  </V2SetupShell>
</template>

<style scoped lang="scss">
  .v2-store-create-page {
    .create-loading {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-2xl tw-flex-col tw-gap-3;
      .loading-title {
        @apply tw-h-8 tw-w-2/3;
      }
      .loading-line {
        @apply tw-h-5 tw-w-full;
      }
      .loading-block {
        @apply tw-h-40 tw-w-full;
      }
    }
    // The gate card sits centred, a comfortable reading width.
    .create-gate {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-lg tw-flex-1 tw-flex-col tw-justify-center tw-py-4;
    }
  }
</style>
